import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from '@reduxjs/toolkit'

import { RootState } from '../store'
import { apiSlice } from './apiSlice'
import { CurrentUser } from './schema/User'
import { Post, Reply } from './schema/Post'
import { NewPost, NewReply } from './types'

interface InitialState {
  loading: {
    main: boolean
    user: boolean
  }
  pagination: {
    homePosts: {
      page: number
    }
    userPosts: {
      page: number
    }
  }
  prevLocation: {
    url: string | null
    home: {
      scroll: string | null
    }
    user: {
      scroll: string | null
    }
  }
  status: {
    redirected: boolean
    error: {
      location: string | null
      status: boolean
      code: number | null
      message: string | null
    }
    success: {
      createAccount: boolean | null
    }
  }
  // user for deletion
  user: CurrentUser | null
}

interface RejectedPayload {
  errorCode: number
  error: any
}

interface LoadingPayload {
  type: string
  status: boolean
}

const initialState: InitialState = {
  loading: {
    main: true,
    user: true,
  },
  pagination: {
    homePosts: {
      page: 1,
    },
    userPosts: {
      page: 1,
    },
  },
  prevLocation: {
    url: null,
    home: {
      scroll: null,
    },
    user: {
      scroll: null,
    },
  },
  status: {
    redirected: false,
    error: {
      location: null,
      status: false,
      code: null,
      message: null,
    },
    success: {
      createAccount: null,
    },
  },
  user: null,
}

export const setNewPostThunk = createAsyncThunk<
  Post,
  NewPost,
  { rejectValue: RejectedPayload }
>('app/setNewPost', async (args: NewPost, thunkAPI) => {
  try {
    const res = thunkAPI.dispatch(
      apiSlice.endpoints.setNewPost.initiate({ ...args }),
    )

    const post = await res.unwrap()

    return post
  } catch (err) {
    return thunkAPI.rejectWithValue(err)
  }
})

export const setNewReplyThunk = createAsyncThunk<
  Reply,
  NewReply,
  { rejectValue: RejectedPayload }
>('app/setNewReply', async (args: NewReply, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as RootState
    const res = thunkAPI.dispatch(
      apiSlice.endpoints.setNewReply.initiate({ ...args }),
    )

    const reply = await res.unwrap()

    return reply
  } catch (err) {
    return thunkAPI.rejectWithValue(err)
  }
})

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<CurrentUser>) {
      state.user = action.payload
    },
    toggleLoading(state, action: PayloadAction<LoadingPayload>) {
      const type = action.payload.type
      if (type === 'main') {
        state.loading.main = action.payload.status
      }
      if (type === 'user') {
        state.loading.user = action.payload.status
      }
    },
    resetLoading(state, action: PayloadAction<string>) {
      // this will reset all loading states depending on where the user is (action.payload)
      // TO DO:
      if (action.payload === 'landing') {
      }
    },
    displayError(
      state,
      action: PayloadAction<{ location: string; message: string }>,
    ) {
      state.status.error.location = action.payload.location
      state.status.error.status = true
      state.status.error.message = action.payload.message
    },
    resetRedirected(state) {
      state.status.redirected = false
      state.status.error.location = null
      state.status.error.status = false
      state.status.error.code = null
      state.status.error.message = null
    },
    setPrevLocation(
      state,
      action: PayloadAction<{
        loc?: string
        scroll: string | null
        url?: string | null
      }>,
    ) {
      if (action.payload.loc === 'home' || action.payload.url === '/home') {
        state.prevLocation.url = action.payload.url as string
        state.prevLocation.home.scroll = action.payload.scroll
      }

      if (action.payload.loc === 'user') {
        state.prevLocation.url = action.payload.url as string
        state.prevLocation.user.scroll = action.payload.scroll
      }
    },
    setPagination(
      state,
      action: PayloadAction<{ loc: string; reset?: boolean }>,
    ) {
      if (action.payload.loc === 'home') {
        if (action.payload.reset) {
          state.pagination.homePosts.page = 1
          return
        }
        state.pagination.homePosts.page += 1
        return
      }

      if (action.payload.loc === 'user') {
        if (action.payload.reset) {
          state.pagination.userPosts.page = 1
          return
        }
        state.pagination.userPosts.page += 1
        return
      }
    },
    resetCreateAccount(state) {
      state.status.success.createAccount = null
      state.status.error.location = null
      state.status.error.status = false
      state.status.error.code = null
      state.status.error.message = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setNewPostThunk.pending, (state) => {})
      .addCase(setNewPostThunk.fulfilled, (state) => {})
      .addCase(setNewPostThunk.rejected, (state) => {})
      .addCase(setNewReplyThunk.pending, (state) => {})
      .addCase(setNewReplyThunk.fulfilled, (state) => {})
      .addCase(setNewReplyThunk.rejected, (state) => {})
  },
})

const selectAppStatus = (state: RootState) => state.app.status

export const selectRedirected = createSelector(
  selectAppStatus,
  (status) => status.redirected,
)

export const {
  setUser,
  toggleLoading,
  displayError,
  resetRedirected,
  setPrevLocation,
  setPagination,
  // setUserLikedPosts,
  resetCreateAccount,
  // resetSignIn,
} = appSlice.actions

export default appSlice.reducer
